import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/colors';
import { useAuth } from '../../contexts/AuthContext';

const AuthContainer = styled.div`
  background: transparent;
  border-radius: 20px;
  padding: 3rem;
  border: none;
  backdrop-filter: none;
  box-shadow: none;
  max-width: 400px;
  margin: 0 auto;
  text-align: center;
`;

const AuthTitle = styled.h3`
  font-family: 'GremeryTwo', serif;
  font-size: 1.8rem;
  color: ${colors.neutral.darkOlive};
  margin-bottom: 1rem;
`;

const AuthForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;
`;

const AuthInputWrapper = styled.div`
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 5px;
    left: 50%;
    transform: translateX(-50%);
    width: 80%;
    height: 15px;
    background-image: url('/images/underline.png');
    background-repeat: no-repeat;
    background-size: 100% 100%;
    background-position: center bottom;
    opacity: 0.7;
    pointer-events: none;
  }
`;

const AuthInput = styled.input`
  padding: 0.75rem 0 1.25rem 0;
  border: none;
  background: transparent;
  font-size: 1rem;
  font-family: 'GremeryOne', cursive;
  line-height: 1.5;
  transition: all 0.3s ease;
  color: ${colors.neutral.darkOlive};
  position: relative;
  width: 100%;
  box-sizing: border-box;
  text-align: center;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: #7f8c8d;
    opacity: 0.8;
    font-family: 'GremeryOne', cursive;
  }
`;

const AuthButton = styled.button`
  background: transparent;
  color: ${colors.neutral.darkOlive};
  padding: 1rem 2rem;
  border-radius: 0;
  font-size: 1.1rem;
  font-family: 'GremeryOne', serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 1px;
  margin-top: 1rem;
  min-width: 140px;

  &:hover {
    background: ${colors.primary.gold};
    color: white;
  }

  &:disabled {
    background: transparent;
    border-color: #ccc;
    cursor: not-allowed;
  }
`;

const AuthErrorMessage = styled.div`
  color: ${colors.api.error};
  font-family: 'GremeryTwo', cursive;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  text-align: center;
  font-style: italic;
  opacity: 0.9;
`;

const AuthFormComponent: React.FC = () => {
  const { isLoading: authLoading, login, error: authError } = useAuth();
  const [password, setPassword] = useState('');

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;
    
    const success = await login(password);
    if (success) {
      setPassword('');
    }
  };

  return (
    <AuthContainer>
      <AuthTitle>Enter Password</AuthTitle>
      
      <AuthForm onSubmit={handleAuthSubmit}>
        <FormGroup>
          <AuthInputWrapper>
            <AuthInput
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={authLoading}
            />
          </AuthInputWrapper>
        </FormGroup>
        
        {authError && (
          <AuthErrorMessage>{authError}</AuthErrorMessage>
        )}
        
        <AuthButton type="submit" disabled={authLoading || !password.trim()}>
          {authLoading ? 'checking' : 'SUBMIT'}
        </AuthButton>
      </AuthForm>
    </AuthContainer>
  );
};

export default AuthFormComponent; 